const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

const rangeProgress = (value, start, end) => clamp((value - start) / (end - start));

const smoothstep = (value) => value * value * (3 - 2 * value);

class TootoAboutImmersive extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.scene = this.querySelector('[data-tooto-about-immersive-scene]');
    this.stage = this.querySelector('[data-tooto-about-immersive-stage]');
    this.media = this.querySelector('[data-tooto-about-immersive-media]');
    this.content = this.querySelector('[data-tooto-about-immersive-content]');
    this.cta = this.querySelector('[data-tooto-about-immersive-cta]');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.staticViewport = window.matchMedia('(max-width: 1024px)').matches;

    if (!this.scene || !this.stage || !this.media || !this.content) return;

    if (this.dataset.scrollEnabled !== 'true' || this.reducedMotion || this.staticViewport) {
      this.showStaticState();
      return;
    }

    this.currentProgress = 0;
    this.targetProgress = 0;
    this.frameRequest = 0;
    this.metricsDirty = true;
    this.contentIsActive = false;

    this.handleViewportChange = this.handleViewportChange.bind(this);
    this.render = this.render.bind(this);
    this.scrollContainer = this.closest('.page-wrapper') || document.querySelector('.page-wrapper');

    this.classList.add('is-ready');
    this.cta?.setAttribute('tabindex', '-1');
    this.content.setAttribute('aria-hidden', 'true');

    window.addEventListener('scroll', this.handleViewportChange, { passive: true });
    window.addEventListener('resize', this.handleViewportChange, { passive: true });
    if (this.scrollContainer) {
      this.scrollContainer.addEventListener('scroll', this.handleViewportChange, { passive: true });
    }

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.metricsDirty = true;
        this.requestRender(true);
      });
      this.resizeObserver.observe(this.stage);
    }

    this.refreshMetrics();
    this.targetProgress = this.getScrollProgress();
    this.currentProgress = this.targetProgress;
    this.applyProgress(this.currentProgress);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleViewportChange);
    window.removeEventListener('resize', this.handleViewportChange);
    this.scrollContainer?.removeEventListener('scroll', this.handleViewportChange);
    this.resizeObserver?.disconnect();
    if (this.frameRequest) cancelAnimationFrame(this.frameRequest);
  }

  showStaticState() {
    this.classList.add('is-static');
    this.style.setProperty('--tooto-about-immersive-media-scale', '1');
    this.style.setProperty('--tooto-about-immersive-media-radius', '0px');
    this.style.setProperty('--tooto-about-immersive-overlay-opacity', String(this.overlayOpacity));
    this.style.setProperty('--tooto-about-immersive-content-opacity', '1');
    this.style.setProperty('--tooto-about-immersive-content-y', '0px');
    this.content?.removeAttribute('aria-hidden');
    this.cta?.removeAttribute('tabindex');
  }

  handleViewportChange(event) {
    if (event?.type === 'resize') this.metricsDirty = true;
    this.requestRender(event?.type === 'resize');
  }

  requestRender(immediate = false) {
    if (immediate) this.renderImmediately = true;
    if (this.frameRequest) return;
    this.frameRequest = requestAnimationFrame(this.render);
  }

  refreshMetrics() {
    this.metricsDirty = false;

    const stageWidth = this.stage.clientWidth;
    const stageHeight = this.stage.clientHeight;
    const mediaWidth = this.media.offsetWidth;
    const initialWidthAt1440 = Number(this.dataset.initialWidth) || 1040;
    const initialWidthMobile = Number(this.dataset.initialWidthMobile) || 89;
    const responsiveWidth = stageWidth <= 767
      ? stageWidth * (initialWidthMobile / 100)
      : stageWidth * (initialWidthAt1440 / 1440);
    const cappedWidth = initialWidthAt1440 * (1760 / 1440);
    const initialWidth = Math.min(responsiveWidth, cappedWidth, mediaWidth * 0.98);

    this.startScale = clamp(initialWidth / Math.max(mediaWidth, 1), 0.05, 0.98);
    this.initialRadius = Number(this.dataset.initialRadius) || 16;
    this.finalOverlayOpacity = clamp((Number(this.dataset.overlayOpacity) || 59) / 100);
    this.scrollableDistance = Math.max(1, this.scene.offsetHeight - stageHeight);
    this.stickyTop = Number.parseFloat(window.getComputedStyle(this.stage).top) || 0;
  }

  get overlayOpacity() {
    return clamp((Number(this.dataset.overlayOpacity) || 59) / 100);
  }

  getScrollProgress() {
    const sceneTop = this.scene.getBoundingClientRect().top;
    return clamp((this.stickyTop - sceneTop) / this.scrollableDistance);
  }

  render() {
    this.frameRequest = 0;
    if (!this.isConnected) return;
    if (this.metricsDirty) this.refreshMetrics();

    this.targetProgress = this.getScrollProgress();

    if (this.renderImmediately) {
      this.currentProgress = this.targetProgress;
      this.renderImmediately = false;
    } else {
      this.currentProgress += (this.targetProgress - this.currentProgress) * 0.16;
    }

    if (Math.abs(this.targetProgress - this.currentProgress) < 0.0005) {
      this.currentProgress = this.targetProgress;
    }

    this.applyProgress(this.currentProgress);

    if (Math.abs(this.targetProgress - this.currentProgress) >= 0.0005) {
      this.requestRender();
    }
  }

  applyProgress(progress) {
    const expandProgress = smoothstep(rangeProgress(progress, 0.04, 0.58));
    const overlayProgress = smoothstep(rangeProgress(progress, 0.58, 0.72));
    const contentProgress = smoothstep(rangeProgress(progress, 0.72, 0.9));
    const mediaScale = this.startScale + (1 - this.startScale) * expandProgress;
    const mediaRadius = (this.initialRadius * (1 - expandProgress)) / Math.max(mediaScale, 0.05);
    const contentY = 36 * (1 - contentProgress);

    this.style.setProperty('--tooto-about-immersive-media-scale', mediaScale.toFixed(5));
    this.style.setProperty('--tooto-about-immersive-media-radius', `${mediaRadius.toFixed(2)}px`);
    this.style.setProperty(
      '--tooto-about-immersive-overlay-opacity',
      (this.finalOverlayOpacity * overlayProgress).toFixed(4)
    );
    this.style.setProperty('--tooto-about-immersive-content-opacity', contentProgress.toFixed(4));
    this.style.setProperty('--tooto-about-immersive-content-y', `${contentY.toFixed(2)}px`);

    const contentShouldBeActive = contentProgress > 0.01;
    if (contentShouldBeActive === this.contentIsActive) return;

    this.contentIsActive = contentShouldBeActive;
    this.content.classList.toggle('is-active', contentShouldBeActive);
    this.content.toggleAttribute('aria-hidden', !contentShouldBeActive);
    if (contentShouldBeActive) this.cta?.removeAttribute('tabindex');
    else this.cta?.setAttribute('tabindex', '-1');
  }
}

if (!customElements.get('tooto-about-immersive')) {
  customElements.define('tooto-about-immersive', TootoAboutImmersive);
}
